import { AbsError, absRequest, mediaUrl } from './client';
import { sessionStore, type AuthSession } from './session';
import type {
  AudioBookmark,
  Author,
  Collection,
  Library,
  LibraryItem,
  LoginResponse,
  MediaProgress,
  PaginatedItems,
  PlaybackSession,
  Series,
  ServerStatus,
  Shelf,
  AbsUser,
} from './types';
import { getDeviceId } from '../lib/device';
import { APP_VERSION } from '../lib/version';

const CLIENT_NAME = 'Plume PWA';
const CLIENT_VERSION = APP_VERSION;

/**
 * Requests that can be slow on large libraries keep at least this timeout,
 * whatever the connection's (shorter) default timeout is.
 */
const HEAVY_TIMEOUT_MS = 30_000;

/** A session used for one request instead of the active one (saved connections, login). */
export type RequestSession = Pick<AuthSession, 'serverUrl' | 'accessToken' | 'refreshToken'> & {
  /** Custom headers of the connection this session belongs to. */
  headers?: Record<string, string>;
};

// ---------------------------------------------------------------- auth

export async function pingServer(serverUrl: string): Promise<ServerStatus> {
  const session = { serverUrl, accessToken: '' };
  // /ping exists on every ABS version; /status adds auth methods and version.
  const ping = await absRequest<{ success?: boolean }>('/ping', { session, anonymous: true, timeoutMs: 8000 });
  // Guard against SPA fallbacks and captive portals answering with HTML.
  if (typeof ping !== 'object' || ping?.success !== true) throw new AbsError('Not an Audiobookshelf server', 0, true);
  try {
    return await absRequest<ServerStatus>('/status', { session, anonymous: true, timeoutMs: 8000 });
  } catch {
    return {};
  }
}

/**
 * Reachability probe for the onboarding URL field (Flutter E4/E5):
 * `/healthcheck`, then `/ping`, then an anonymous `/api/authorize`. Any HTTP
 * answer counts as reachable; only network failures (offline, DNS, CORS) don't.
 */
export async function checkReachability(serverUrl: string, signal?: AbortSignal): Promise<boolean> {
  const session = { serverUrl, accessToken: '' };
  const probes: Array<[string, 'GET' | 'POST']> = [
    ['/healthcheck', 'GET'],
    ['/ping', 'GET'],
    ['/api/authorize', 'POST'],
  ];
  for (const [path, method] of probes) {
    try {
      await absRequest(path, { method, session, anonymous: true, timeoutMs: 8000, signal });
      return true;
    } catch (err) {
      if (signal?.aborted) return false;
      if (err instanceof AbsError && err.status > 0) return true;
    }
  }
  return false;
}

function sessionFromLogin(serverUrl: string, data: LoginResponse, fallbackToken?: string): AuthSession {
  const user = data.user;
  const accessToken = user.accessToken ?? user.token ?? fallbackToken;
  if (!accessToken) throw new Error('The server did not return an access token.');
  return {
    serverUrl,
    userId: user.id,
    username: user.username,
    accessToken,
    refreshToken: user.refreshToken,
    defaultLibraryId: data.userDefaultLibraryId,
    serverVersion: data.serverSettings?.version,
  };
}

export interface Authenticated {
  session: AuthSession;
  user: AbsUser;
}

/** Username/password sign-in that does not touch the active session. */
export async function authenticate(serverUrl: string, username: string, password: string, headers: Record<string, string> = {}): Promise<Authenticated> {
  const data = await absRequest<LoginResponse>('/login', {
    method: 'POST',
    session: { serverUrl, accessToken: '', headers },
    anonymous: true,
    // Ask ABS 2.26+ to include the refresh token in the body (we don't use cookies).
    headers: { 'x-return-tokens': 'true' },
    body: { username, password },
  });
  return { session: { ...sessionFromLogin(serverUrl, data), authType: 'password' }, user: data.user };
}

/** API key / legacy token sign-in that does not touch the active session. */
export async function authenticateWithToken(serverUrl: string, token: string, headers: Record<string, string> = {}): Promise<Authenticated> {
  const data = await absRequest<LoginResponse>('/api/authorize', {
    method: 'POST',
    session: { serverUrl, accessToken: token, headers },
  });
  const session = sessionFromLogin(serverUrl, data, token);
  // An API key must be used as-is; don't swap it for the user's legacy token.
  return { session: { ...session, accessToken: token, refreshToken: undefined, authType: 'apiKey' }, user: data.user };
}

/** Revokes the refresh token server-side (best effort). Does not change the active session. */
export async function revokeSession(session: RequestSession): Promise<void> {
  if (!session.refreshToken) return;
  await absRequest('/logout', {
    method: 'POST',
    session,
    anonymous: true,
    headers: { 'x-refresh-token': session.refreshToken },
    timeoutMs: 5000,
  }).catch(() => undefined);
}

/** `POST /api/authorize`; the payload carries every progress entry, so it gets the long timeout. */
export function authorize(session?: RequestSession): Promise<LoginResponse> {
  return absRequest<LoginResponse>('/api/authorize', { method: 'POST', session, timeoutMs: HEAVY_TIMEOUT_MS });
}

/**
 * Verifies a session (Flutter E2 → E3): `POST /api/authorize`, falling back to
 * `GET /api/me` when the authorize route itself fails for a non-auth reason.
 */
export async function verifySession(session: RequestSession): Promise<LoginResponse> {
  try {
    return await authorize(session);
  } catch (err) {
    if (err instanceof AbsError && (err.isNetworkError || err.status === 401 || err.status === 403)) throw err;
    const user = await absRequest<AbsUser>('/api/me', { session });
    return { user };
  }
}

export function getMe(session?: RequestSession): Promise<AbsUser> {
  return absRequest<AbsUser>('/api/me', { session, timeoutMs: HEAVY_TIMEOUT_MS });
}

// ---------------------------------------------------------------- libraries

function normalizeLibrary(raw: Library): Library | null {
  const id = String(raw.id ?? '').trim();
  const name = String(raw.name ?? raw.title ?? '').trim();
  if (!id || !name) return null;
  return { ...raw, id, name, mediaType: raw.mediaType ?? 'book' };
}

/** Every library on the server, sorted by `displayOrder`; blank ids/names dropped. */
export async function getLibraries(session?: RequestSession): Promise<Library[]> {
  const data = await absRequest<{ libraries?: Library[] }>('/api/libraries', { session });
  return (data.libraries ?? [])
    .map(normalizeLibrary)
    .filter((l): l is Library => l !== null)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

const LIBRARIES_TTL_MS = 2 * 60_000;
const librariesCache = new Map<string, { at: number; libraries: Library[] }>();
const librariesInFlight = new Map<string, Promise<Library[]>>();

/** `getLibraries` with Flutter's 2-minute cache per server + token and in-flight de-duplication. */
export function getLibrariesCached(session?: RequestSession, opts: { force?: boolean } = {}): Promise<Library[]> {
  const s = session ?? sessionStore.get();
  if (!s) return Promise.reject(new AbsError('Not signed in', 401));
  const key = `${s.serverUrl}|${s.accessToken}`;
  const hit = librariesCache.get(key);
  if (!opts.force && hit && Date.now() - hit.at <= LIBRARIES_TTL_MS) return Promise.resolve([...hit.libraries]);
  const pending = librariesInFlight.get(key);
  if (pending) return pending;
  const promise = getLibraries(session)
    .then((libraries) => {
      librariesCache.set(key, { at: Date.now(), libraries });
      return [...libraries];
    })
    .finally(() => librariesInFlight.delete(key));
  librariesInFlight.set(key, promise);
  return promise;
}

export function clearLibrariesCache(): void {
  librariesCache.clear();
}

/** Home shelves (Flutter E11 sends `limit=10`). Filtering into book shelves happens in `data/home.ts`. */
export function getPersonalized(libraryId: string, limit = 10): Promise<Shelf[]> {
  return absRequest<Shelf[]>(`/api/libraries/${libraryId}/personalized`, { query: { limit }, timeoutMs: HEAVY_TIMEOUT_MS });
}

export interface ItemsQuery {
  page?: number;
  /** `0` omits the parameter (ABS then returns every match). */
  limit?: number;
  sort?: string;
  desc?: boolean;
  filter?: string;
  collapseseries?: boolean;
  /** E.g. `progress`. */
  include?: string;
  /** Defaults to true (lighter payload without audio files). */
  minified?: boolean;
}

export function getLibraryItems(libraryId: string, q: ItemsQuery = {}): Promise<PaginatedItems<LibraryItem>> {
  const limit = q.limit ?? 50;
  return absRequest(`/api/libraries/${libraryId}/items`, {
    query: {
      limit: limit > 0 ? limit : undefined,
      page: q.page ?? 0,
      sort: q.sort ?? 'media.metadata.title',
      desc: q.desc ? 1 : 0,
      filter: q.filter,
      collapseseries: q.collapseseries ? 1 : 0,
      include: q.include,
      minified: q.minified === false ? undefined : 1,
    },
    timeoutMs: HEAVY_TIMEOUT_MS,
  });
}

/** Flutter's sort names (BookFilterOptions.sortBy) mapped to ABS item sort keys. */
export const ABS_SORT_KEYS = {
  dateAdded: 'addedAt',
  title: 'media.metadata.title',
  duration: 'media.duration',
  author: 'media.metadata.authorName',
  narrator: 'media.metadata.narratorName',
  dateReleased: 'media.metadata.publishedYear',
  lastPlayed: 'progress',
  fileSize: 'size',
  updatedAt: 'updatedAt',
} as const;

/**
 * Page size and sort the Books sync uses (Flutter: 200 per page, `updatedAt:desc`, `include=progress`).
 * The service worker never caches these pages (`src/sw.ts`): offline, a sync
 * must fail instead of replaying pages of an older sync.
 */
export const LIBRARY_SYNC_PAGE_SIZE = 200;

export function getLibraryItemsForSync(libraryId: string, page: number, signal?: AbortSignal): Promise<PaginatedItems<LibraryItem>> {
  return absRequest(`/api/libraries/${libraryId}/items`, {
    query: { sort: ABS_SORT_KEYS.updatedAt, desc: 1, page, limit: LIBRARY_SYNC_PAGE_SIZE, include: 'progress' },
    signal,
    timeoutMs: 30_000,
  });
}

/** ABS filter values are base64 encoded, e.g. `series.<b64(id)>` (URL-encoding happens in withQuery). */
export function encodeFilter(group: 'series' | 'authors' | 'genres' | 'tags' | 'narrators' | 'progress', value: string): string {
  const bytes = new TextEncoder().encode(value);
  const b64 = btoa(String.fromCharCode(...bytes));
  return `${group}.${b64}`;
}

// ---------------------------------------------------------------- series

export interface SeriesListQuery {
  page?: number;
  limit?: number;
  sort?: string;
  desc?: boolean;
  include?: string;
  minified?: boolean;
}

/**
 * One page of series as Flutter requests it (E14): newest first with progress
 * and books embedded. `page` / `limit` are omitted when 0.
 */
export async function getSeriesPage(libraryId: string, q: SeriesListQuery = {}): Promise<Series[]> {
  const page = q.page ?? 0;
  const limit = q.limit ?? 100;
  const data = await absRequest<{ results?: Series[] }>(`/api/libraries/${libraryId}/series`, {
    query: {
      sort: q.sort ?? 'addedAt',
      desc: (q.desc ?? true) ? 1 : 0,
      include: q.include ?? 'progress,books',
      limit: limit > 0 ? limit : undefined,
      page: page > 0 ? page : undefined,
    },
    timeoutMs: HEAVY_TIMEOUT_MS,
  });
  return data.results ?? [];
}

/** Home "Recent Series": 10 newest. */
export function getRecentSeries(libraryId: string, limit = 10): Promise<Series[]> {
  return getSeriesPage(libraryId, { limit });
}

const MAX_PAGES = 200;

/**
 * Pages through a list until a page is empty, shorter than the page size, or
 * adds no new ids, with a 200-page safeguard (Flutter `getAllSeries` and friends).
 */
async function pageThrough<T extends { id: string }>(pageSize: number, fetchPage: (page: number) => Promise<T[]>): Promise<T[]> {
  const out: T[] = [];
  const seen = new Set<string>();
  for (let page = 0; page < MAX_PAGES; page++) {
    const batch = await fetchPage(page);
    if (batch.length === 0) break;
    let added = 0;
    for (const entry of batch) {
      const id = String(entry.id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(entry);
      added++;
    }
    if (batch.length < pageSize || added === 0) break;
  }
  return out;
}

/** Every series in the library, newest first (Series tab). */
export function getAllSeries(libraryId: string, pageSize = 100): Promise<Series[]> {
  return pageThrough(pageSize, (page) => getSeriesPage(libraryId, { page, limit: pageSize }));
}

type BookEntry = Record<string, unknown>;

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
}

function stringIds(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'string' || typeof x === 'number' ? String(x).trim() : '')).filter((x) => x && x !== 'null');
  if (typeof v === 'string' || typeof v === 'number') {
    const s = String(v).trim();
    return s && s !== 'null' ? [s] : [];
  }
  return [];
}

function withFinishedFlag(item: LibraryItem): LibraryItem {
  const prev = item.userMediaProgress;
  return {
    ...item,
    userMediaProgress: {
      libraryItemId: item.id,
      duration: prev?.duration ?? item.media?.duration ?? 0,
      progress: 1,
      currentTime: prev?.currentTime ?? 0,
      lastUpdate: prev?.lastUpdate ?? 0,
      ...prev,
      isFinished: true,
    },
  };
}

/** Item details with per-user progress (E9); 404 resolves to null. */
export async function getItemOrNull(itemId: string, opts: { expanded?: boolean } = {}): Promise<LibraryItem | null> {
  try {
    return await getItem(itemId, opts);
  } catch (err) {
    if (err instanceof AbsError && err.status === 404) return null;
    throw err;
  }
}

export interface SeriesDetail {
  /** The series (or collection) record as the server returned it. */
  group: (Series | Collection) | null;
  kind: 'series' | 'collection';
  /** Books in server order, finished ids applied. */
  items: LibraryItem[];
}

/**
 * Series or collection with its books, from one detail request plus per-item
 * fallbacks. Collections are recognised by `kind` or a `col_` id (Flutter).
 */
export async function getSeriesDetail(seriesId: string, fallbackItemIds: readonly string[] = [], kind?: 'series' | 'collection'): Promise<SeriesDetail> {
  const id = seriesId.trim();
  if ((kind ?? (id.startsWith('col_') ? 'collection' : 'series')) === 'collection') {
    const data = asRecord(await absRequest(`/api/collections/${id}`, { timeoutMs: HEAVY_TIMEOUT_MS })) ?? {};
    const collection = (asRecord(data.collection) ?? data) as unknown as Collection;
    const books = collectionBooksOf(data);
    return { group: collection, kind: 'collection', items: books.length > 0 ? books : await fetchItemsById(fallbackItemIds, new Set()) };
  }

  const data = asRecord(await absRequest(`/api/series/${id}`, { query: { include: 'progress,books' }, timeoutMs: HEAVY_TIMEOUT_MS })) ?? {};
  return { group: data as unknown as Series, kind: 'series', items: await seriesBooksFrom(data, fallbackItemIds) };
}

async function seriesBooksFrom(data: Record<string, unknown>, fallbackItemIds: readonly string[]): Promise<LibraryItem[]> {
  const scopes = [data, asRecord(data.series)].filter((x): x is Record<string, unknown> => !!x);
  const finishedIds = new Set<string>();
  for (const scope of scopes) stringIds(asRecord(scope.progress)?.libraryItemIdsFinished).forEach((x) => finishedIds.add(x));

  const books: LibraryItem[] = [];
  const seen = new Set<string>();
  const fallbackIds: string[] = [];
  const addFallback = (ids: string[]) => ids.forEach((x) => !fallbackIds.includes(x) && fallbackIds.push(x));

  for (const scope of scopes) {
    for (const key of ['libraryItems', 'books', 'items']) {
      const list = scope[key];
      if (!Array.isArray(list) || list.length === 0) continue;
      for (const raw of list as BookEntry[]) {
        const entry = asRecord(raw);
        if (!entry) {
          addFallback(stringIds(raw));
          continue;
        }
        const nested = asRecord(entry.libraryItem);
        let item: LibraryItem | null = null;
        if (nested && asRecord(nested.media)) {
          item = { ...(nested as unknown as LibraryItem) };
          if (!item.userMediaProgress && asRecord(entry.userMediaProgress)) item.userMediaProgress = entry.userMediaProgress as MediaProgress;
        } else if (asRecord(entry.media)) {
          item = entry as unknown as LibraryItem;
        }
        if (item) {
          if (seen.has(item.id)) continue;
          seen.add(item.id);
          books.push(finishedIds.has(item.id) ? withFinishedFlag(item) : item);
        } else {
          addFallback(stringIds(nested?.id ?? entry.libraryItemId ?? entry.libraryItemID ?? entry.itemId ?? entry.libraryItem));
        }
      }
    }
  }
  for (const scope of scopes) {
    const progress = asRecord(scope.progress);
    addFallback(stringIds(scope.libraryItemIds));
    addFallback(stringIds(progress?.libraryItemIds));
    addFallback(stringIds(progress?.libraryItemId));
    addFallback(stringIds(progress?.itemId));
  }

  if (books.length > 0) return books;
  const fetched = await fetchItemsById(fallbackIds, finishedIds);
  if (fetched.length > 0) return fetched;
  return fetchItemsById(fallbackItemIds, finishedIds);
}

async function fetchItemsById(ids: readonly string[], finishedIds: Set<string>): Promise<LibraryItem[]> {
  const out: LibraryItem[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    const item = await getItemOrNull(id, { expanded: false });
    if (item) out.push(finishedIds.has(item.id) ? withFinishedFlag(item) : item);
  }
  return out;
}

// ---------------------------------------------------------------- authors

export interface AuthorsQuery {
  page?: number;
  limit?: number;
  sort?: string;
  desc?: boolean;
}

/** One page of authors (E16), newest first by default. */
export async function getAuthorsPage(libraryId: string, q: AuthorsQuery = {}): Promise<Author[]> {
  const page = q.page ?? 0;
  const limit = q.limit ?? 100;
  const data = await absRequest<{ authors?: Author[]; results?: Author[] }>(`/api/libraries/${libraryId}/authors`, {
    query: {
      sort: q.sort ?? 'addedAt',
      desc: (q.desc ?? true) ? 1 : 0,
      limit: limit > 0 ? limit : undefined,
      page: page > 0 ? page : undefined,
    },
    timeoutMs: HEAVY_TIMEOUT_MS,
  });
  return data.authors ?? data.results ?? [];
}

/** Home "Newest Authors": 10 newest. */
export async function getRecentAuthors(libraryId: string, limit = 10): Promise<Author[]> {
  return (await getAuthorsPage(libraryId, { limit })).slice(0, limit);
}

/** Every author, paging until the list is exhausted (works whether or not the server honours `limit`). */
export function getAllAuthors(libraryId: string, pageSize = 100): Promise<Author[]> {
  return pageThrough(pageSize, (page) => getAuthorsPage(libraryId, { page, limit: pageSize }));
}

/** Author record only (name, image, counts). */
export function getAuthorInfo(authorId: string): Promise<Author> {
  return absRequest(`/api/authors/${authorId}`);
}

/** All books by an author, newest first, no page limit (Flutter E8). */
export async function getAuthorBooks(libraryId: string, authorId: string): Promise<LibraryItem[]> {
  const data = await getLibraryItems(libraryId, {
    limit: 0,
    page: 0,
    sort: ABS_SORT_KEYS.dateAdded,
    desc: true,
    filter: encodeFilter('authors', authorId),
    include: 'progress',
  });
  return data.results ?? [];
}

// ---------------------------------------------------------------- collections

export interface CollectionsQuery {
  page?: number;
  limit?: number;
}

const collectionAddedAt = (c: Collection): number => c.addedAt ?? c.lastUpdate ?? c.createdAt ?? 0;

/** Newest first, then title A-Z (Flutter's client-side collection order). */
export function sortCollections<T extends Collection>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const byDate = collectionAddedAt(b) - collectionAddedAt(a);
    if (byDate !== 0) return byDate;
    const at = a.name.toLowerCase();
    const bt = b.name.toLowerCase();
    return at < bt ? -1 : at > bt ? 1 : 0;
  });
}

/** One page of collections (E15). */
export async function getCollectionsPage(libraryId: string, q: CollectionsQuery = {}): Promise<Collection[]> {
  const page = q.page ?? 0;
  const limit = q.limit ?? 100;
  const data = await absRequest<{ results?: Collection[]; collections?: Collection[] }>(`/api/libraries/${libraryId}/collections`, {
    query: { limit: limit > 0 ? limit : undefined, page: page > 0 ? page : undefined },
    timeoutMs: HEAVY_TIMEOUT_MS,
  });
  return sortCollections(data.results ?? data.collections ?? []);
}

/** Every collection, paged 100 at a time, newest first. */
export async function getAllCollections(libraryId: string, pageSize = 100): Promise<Collection[]> {
  return sortCollections(await pageThrough(pageSize, (page) => getCollectionsPage(libraryId, { page, limit: pageSize })));
}

function collectionBooksOf(data: Record<string, unknown>): LibraryItem[] {
  const list = (Array.isArray(data.books) ? data.books : asRecord(data.collection)?.books) as LibraryItem[] | undefined;
  const seen = new Set<string>();
  return (list ?? []).filter((b) => b && typeof b === 'object' && !seen.has(b.id) && !!seen.add(b.id));
}

// ---------------------------------------------------------------- items

/** Item details. `expanded` (default) adds audio files, tracks and chapters; progress is always included. */
export function getItem(itemId: string, opts: { expanded?: boolean } = {}): Promise<LibraryItem> {
  return absRequest(`/api/items/${itemId}`, { query: { expanded: opts.expanded === false ? undefined : 1, include: 'progress' } });
}

/**
 * Full items (audio files, tracks and chapters) for several ids in one request
 * (`POST /api/items/batch/get`, ABS's expanded JSON). Ids the server does not
 * return (deleted, or not visible to this user) are simply missing.
 */
export async function getItemsBatch(itemIds: readonly string[], signal?: AbortSignal): Promise<LibraryItem[]> {
  if (itemIds.length === 0) return [];
  const data = await absRequest<{ libraryItems?: LibraryItem[] } | null>('/api/items/batch/get', {
    method: 'POST',
    body: { libraryItemIds: itemIds },
    signal,
    timeoutMs: HEAVY_TIMEOUT_MS,
  });
  return Array.isArray(data?.libraryItems) ? data.libraryItems.filter((item) => item && typeof item.id === 'string') : [];
}

export function coverUrl(item: Pick<LibraryItem, 'id' | 'updatedAt'> | string, width = 400): string {
  const id = typeof item === 'string' ? item : item.id;
  const ts = typeof item === 'string' ? undefined : item.updatedAt;
  return mediaUrl(`/api/items/${id}/cover`, { width, format: 'webp', ts });
}

/** Large artwork (Flutter `highResCoverUrl`: `width=1000`). */
export function highResCoverUrl(item: Pick<LibraryItem, 'id' | 'updatedAt'> | string): string {
  return coverUrl(item, 1000);
}

export function authorImageUrl(author: Pick<Author, 'id' | 'imagePath'>, width = 200): string {
  if (!author.imagePath) return '';
  return mediaUrl(`/api/authors/${author.id}/image`, { width, format: 'webp' });
}

// ---------------------------------------------------------------- playback

export function startPlaybackSession(itemId: string, supportedMimeTypes: string[], forceTranscode = false): Promise<PlaybackSession> {
  return absRequest(`/api/items/${itemId}/play`, {
    method: 'POST',
    timeoutMs: 30000,
    body: {
      deviceInfo: {
        deviceId: getDeviceId(),
        clientName: CLIENT_NAME,
        clientVersion: CLIENT_VERSION,
        manufacturer: navigator.vendor || undefined,
        model: navigator.platform || undefined,
      },
      supportedMimeTypes,
      mediaPlayer: 'html5',
      forceDirectPlay: false,
      forceTranscode,
    },
  });
}

export function syncSession(sessionId: string, currentTime: number, timeListened: number, duration: number): Promise<void> {
  return absRequest(`/api/session/${sessionId}/sync`, {
    method: 'POST',
    body: { currentTime, timeListened, duration },
    timeoutMs: 10000,
  });
}

export function closeSession(sessionId: string, currentTime: number, timeListened: number, duration: number): Promise<void> {
  return absRequest(`/api/session/${sessionId}/close`, {
    method: 'POST',
    body: { currentTime, timeListened, duration },
    timeoutMs: 10000,
  });
}

/** Records listening done offline as a closed session so ABS stats stay accurate. */
export function syncLocalSession(session: Record<string, unknown>): Promise<void> {
  return absRequest('/api/session/local', { method: 'POST', body: session, timeoutMs: 15000 });
}

export function updateProgress(
  itemId: string,
  payload: Partial<Pick<MediaProgress, 'currentTime' | 'duration' | 'progress' | 'isFinished' | 'hideFromContinueListening'>> & { lastUpdate?: number },
): Promise<void> {
  return absRequest(`/api/me/progress/${itemId}`, { method: 'PATCH', body: payload, timeoutMs: 10000 });
}

/** "Mark as Finished" (E18). */
export function markFinished(itemId: string): Promise<void> {
  return updateProgress(itemId, { isFinished: true });
}

/** "Mark as Unfinished" (E19). */
export function markUnfinished(itemId: string): Promise<void> {
  return updateProgress(itemId, { isFinished: false });
}

/** "Reset Progress" (E20). */
export function resetProgress(itemId: string): Promise<void> {
  return updateProgress(itemId, { isFinished: false, currentTime: 0 });
}

export function getProgress(itemId: string): Promise<MediaProgress> {
  return absRequest(`/api/me/progress/${itemId}`);
}

// ---------------------------------------------------------------- bookmarks

export function createBookmark(itemId: string, time: number, title: string): Promise<AudioBookmark> {
  return absRequest(`/api/me/item/${itemId}/bookmark`, { method: 'POST', body: { time, title } });
}

export function deleteBookmark(itemId: string, time: number): Promise<void> {
  return absRequest(`/api/me/item/${itemId}/bookmark/${time}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------- downloads

/** Server path for a single audio file; supports HTTP Range requests. */
export function audioFilePath(itemId: string, ino: string): string {
  return `/api/items/${itemId}/file/${ino}`;
}
